import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { v1 as uuidv1 } from 'uuid';
import JustificationContainer from '~/components/atom/JustificationContainer';
import type { SystemSize } from '~/theme';

export type Props = {
  checked: boolean;

  /** Set data-testid */

  /** Set data-objectid */
  dataObjectId?: string;
  onChange?: (arg0: React.SyntheticEvent<any> | any) => void;

  /** Label to be displayed */
  label?: string | null;

  /** Name param should be used the same for all radio buttons in same set */
  name?: string;

  /** Disable radiobutton */
  disabled?: boolean;

  className?: string;

  /** Margin of the container */
  margin?: Array<SystemSize | null>;
};

/**
 * RadioButton Element
 *
 * Should pretty much always be used in combination with a InputGroup
 * in order to have consistent styling.
 */
const RadioButton: React.FCC<Props> = ({
  dataTestId,
  name,
  dataObjectId,
  label,
  disabled,
  className,
  margin = [null],
  onChange,
  ...rest
}) => {
  const id = useMemo(() => uuidv1(), []);
  const saveName = name || uuidv1();
  const labelTestId = dataTestId ? dataTestId + '-label' : '';

  return (
    <Container
      data-objectid={dataObjectId}
      className={className}
      margin={margin}
      direction="column"
      width="max-content"
      {...rest}
    >
      <RadioButtonElement
        {...rest}
        id={id}
        type="radio"
        name={saveName}
        onChange={onChange}
        data-testid={dataTestId}
        disabled={disabled}
      />
      <label htmlFor={id} data-testid={labelTestId}>
        {label}
      </label>
    </Container>
  );
};

const Container = styled(JustificationContainer)<{ $margin?: boolean }>(
  () => css`
    position: relative;
  `,
);

type RadioButtonElementProps = {
  disabled?: boolean;
};
const RadioButtonElement = styled.input<RadioButtonElementProps>(
  ({ theme, disabled }) => css`
    /* "visually hidden" or "screen reader only" technique to hide the item */
    opacity: 0;
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;

    & + label {
      position: relative;
      padding-left: 30px;
      cursor: pointer;
      min-height: 20px;
      display: inline-flex;
      align-items: center;

      ${disabled &&
      css`
        pointer-events: none;
        opacity: 0.5;
      `};
    }

    & + label:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 20px;
      height: 20px;
      box-sizing: border-box;
      border: 1px solid ${theme.color('secondary')};
      border-radius: ${theme.getTokens().border.radius.full};
      background: ${theme.color('secondary', 'translucent')};
    }

    &:checked + label:before {
      border: 3px solid ${theme.color('secondary')};
    }

    & + label:after {
      content: '';
      width: ${theme.space('xs')};
      height: ${theme.space('xs')};
      background: ${theme.color('secondary')};
      position: absolute;
      top: 10px;
      left: 10px;
      border-radius: ${({ theme }) => theme.getTokens().border.radius.full};
      transform: translate(-50%, -50%) scale(1);
      transition:
        opacity 0.2s ease,
        transform 0.2s ease;
    }

    &:not(:checked) + label:after {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0);
    }

    &:checked + label:after {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  `,
);

export default RadioButton;
